import { useCallback } from 'react'

import { TextFieldProps, TextField as TidalTextField } from '@workwave-tidal/core/components'
import {
  FormFieldDescriptor,
  type NarrowFieldsByValueType,
  TidalFormBridgeOptions,
  TidalFormState,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'

import { FormControl, FormControlLabelProps, FormHelperText } from '@mui/material'

import { BulkFieldValue } from './types'

export type BulkTextFieldValue = BulkFieldValue<string>
const defaultComponentValue = {
  status: 'mixed',
}

export type BulkTextFieldProps<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = BulkTextFieldValue,
> = {
  formControlLabelProps?: Partial<FormControlLabelProps>
  bulkTextFieldProps?: Partial<Omit<TextFieldProps, 'options' | 'value' | 'onChange'>>
  // label to use in case of mixed value
  mixedLabel: string
} & TidalFormBridgeOptions<BulkTextFieldValue, TargetField, State, AcceptedFormValue>

export function BulkTextField<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = BulkTextFieldValue,
>(props: BulkTextFieldProps<TargetField, State, AcceptedFormValue>) {
  const {
    bulkTextFieldProps = {},
    formControlLabelProps = {},
    mixedLabel,
    initialValue = defaultComponentValue as FormFieldDescriptor<State>[TargetField]['value'],
    validateOn = 'change',
    ...bridgeOptions
  } = props

  const {
    config: { fullWidth, label, testId = 'bulk-textfield-root' },
    fieldInfo: {
      errorOrHelperText,
      field: { visible, required },
      fieldDisabled,
      fieldHasError,
      onBlur,
      onChange,
      onFocus,
      showHelperText,
      value,
    },
  } = useTidalFormBridge<BulkTextFieldValue, TargetField, State, AcceptedFormValue>({
    ...bridgeOptions,
    validateOn,
    initialValue,
  })

  const handleOnChange = useCallback(
    event => {
      onChange({ status: 'exact', value: event.target.value })
    },
    [onChange],
  )

  if (!visible) return null

  return (
    <FormControl
      error={fieldHasError}
      component="fieldset"
      fullWidth={fullWidth}
      data-testid={testId}
      required={required}
      data-tidal-form-field-name={bridgeOptions.name}
      sx={{
        '&.tidal-form-devtool-highlight': theme => ({
          outline: `4px solid var(--tidal-devtools-highlight-color, ${theme.vars.palette.error.dark})`,
        }),
      }}
    >
      <TidalTextField
        {...bulkTextFieldProps}
        label={label}
        required={required}
        placeholder={value.status === 'mixed' ? mixedLabel : value.value}
        value={value.status === 'mixed' ? undefined : value.value}
        onChange={handleOnChange}
        onBlur={onBlur}
        onFocus={onFocus}
        disabled={fieldDisabled}
        InputLabelProps={{
          shrink: value.status === 'mixed' || (value.status === 'exact' && value.value.length > 0),
        }}
      />

      {showHelperText && <FormHelperText error={fieldHasError}>{errorOrHelperText}</FormHelperText>}
    </FormControl>
  )
}
